import { z } from 'zod';
import {
  createModel,
  imageSchema,
  partySchema,
  refSchema,
  tenantSchema,
  termDurationSchema,
} from '../../common';
import { dateSchema, priceSchema } from '../../validation';
import {
  financingApplicationStatusSchema,
  financingApplicationTagSchema,
} from './financing.enums';
import { financingInvoiceSchema } from './financing.invoice.model';
import { financingOfferSchema } from './financing.offer.model';
import { orgFinancingProfileSchema } from './financing.profile.model';

/**
 * Identifies the actor that produced an event. Server-stamped at
 * write time — never trusted from the client.
 *
 * Shape:
 *  - `id`         — `ObjectId.toHexString()` for users / lenders;
 *                   `"system"` for cron / migration writes.
 *  - `name`       — display name shown in admin event log; the
 *                   user's full name, the lender's company name, or
 *                   `"System"` for automated writes.
 *  - `isSuperAdmin` — whether the actor was acting with super-admin
 *                   privileges. Lets the audit tab visually flag
 *                   ops actions vs lender / carrier actions without
 *                   round-tripping to Org for permission lookup.
 *
 * String form (legacy) is still accepted by Zod for back-compat
 * with rows written before the migration; the admin UI normalises
 * it on read.
 */
export const financingApplicationEventActorSchema = z.union([
  z.object({
    id: z.string().trim().min(1),
    name: z.string().trim().min(1),
    isSuperAdmin: z.boolean().default(false),
  }),
  z.string().trim().min(1),
]);

export type FinancingApplicationEventActor = z.infer<
  typeof financingApplicationEventActorSchema
>;

/**
 * Lifecycle event recorded for audit / debugging on each
 * application. Append-only by convention; the admin "Events" tab
 * renders this directly while the carrier UI only consumes the
 * derived status.
 */
export const financingApplicationEventSchema = z.object({
  at: dateSchema,
  /**
   * Coarse event type. Add new values here as the lifecycle grows
   * (e.g. `lender_invited`, `offer_received`, `offer_accepted`,
   * `funded`, `repaid`, `defaulted`).
   */
  type: z.string().trim().min(1),
  /**
   * Actor that produced the event. See
   * {@link financingApplicationEventActorSchema}. Optional for
   * historical rows; every new write fills it in.
   */
  by: financingApplicationEventActorSchema.optional(),
  /** Free-form structured payload for the event. */
  payload: z.record(z.unknown()).optional(),
});

export type FinancingApplicationEvent = z.infer<
  typeof financingApplicationEventSchema
>;

/**
 * Outcome of an accepted application. Filled in by Hero ops as the
 * loan moves through funding → repayment → close.
 *
 * Every field is optional so the same shape can be progressively
 * filled across the lifecycle. The admin "Record funding",
 * "Mark repaid" and "Mark defaulted" dialogs write here; only the
 * subset relevant to the kind being marked is touched per call.
 *
 * Naming reflects the contract / operational split:
 *  - **Acceptance** is a contract event — terms (advance, fee, %,
 *    repayment date) live on {@link financingOfferTermsSchema}
 *    and are locked at the offer whose `decision === 'accepted'`.
 *    There is intentionally NO duplication of those numbers here;
 *    the admin UI reads them from the accepted offer.
 *  - **Funding** is an operational event — date + optional proof.
 *  - **Repayment / Default** mirror that shape — date + optional
 *    proof + an outstanding amount on the default leg.
 *
 * Image fields (`fundingProof`, `repaymentProof`) are uploaded
 * through {@link ImageService} into dedicated Cloudinary folders.
 * Carrier-uploaded repayment screenshots live separately on
 * {@link financingRepaymentReceiptSchema} so the admin can
 * cross-check both views without losing audit history.
 */
export const financingApplicationOutcomeSchema = z.object({
  /* ── funded ── */
  /** When the lender disbursed the funds (admin-supplied). */
  fundedAt: dateSchema.optional(),
  /**
   * Admin-uploaded proof of funding (wire receipt, lender
   * statement, etc.). Optional — single image. Audit-only; the
   * carrier dashboard does NOT surface this image to the carrier.
   */
  fundingProof: imageSchema.optional(),

  /* ── repaid ── */
  /** When the loan was repaid in full (admin-supplied). */
  repaidAt: dateSchema.optional(),
  /**
   * Admin-uploaded proof of repayment (lender statement, bank
   * confirmation). Optional — single image. Distinct from the
   * carrier-uploaded {@link financingRepaymentReceiptSchema}: this
   * is Hero ops' own attestation, that one is the carrier's.
   */
  repaymentProof: imageSchema.optional(),

  /* ── defaulted ── */
  /** When the application was declared in default (admin-supplied). */
  defaultedAt: dateSchema.optional(),
  /** Outstanding amount at default time. */
  defaultedAmount: priceSchema.optional(),

  /** Free-form ops note appended on each mark-outcome write. */
  notes: z.string().trim().optional(),
});

export type FinancingApplicationOutcome = z.infer<
  typeof financingApplicationOutcomeSchema
>;

/**
 * Carrier-uploaded proof that they repaid the lender. Captured
 * after `funded` so the admin / lender can flip
 * `funded → awaiting_repayment_approval → repaid`.
 *
 *  - **`image`** — REQUIRED — the receipt screenshot / bank
 *    transfer confirmation is the entire point.
 *  - **`notes`** — optional carrier commentary surfaced to the
 *    reviewer (admin or lender).
 *  - **`repaidAt`** — optional carrier-declared date the wire
 *    actually cleared. Surfaced to the reviewer side-by-side with
 *    the upload so they can sanity-check the receipt against the
 *    carrier's own claim. Distinct from `outcome.repaidAt` (the
 *    canonical "loan closed" timestamp), which is only stamped
 *    when the reviewer approves.
 *  - **`submittedAt`** — server-stamped at upload time. The
 *    reviewer compares this with `repaidAt` to spot back-dated
 *    claims.
 */
export const financingRepaymentReceiptSchema = z.object({
  image: imageSchema,
  notes: z.string().trim().optional(),
  repaidAt: dateSchema.optional(),
  submittedAt: dateSchema,
});

export type FinancingRepaymentReceipt = z.infer<
  typeof financingRepaymentReceiptSchema
>;

/**
 * `FinancingApplication db model` — the single collection that
 * does everything for the v0 invoice-factoring pilot.
 *
 * Lifecycle:
 *
 *   carrier submits          →  status: 'pending_review'
 *   admin sends to lenders   →  status: 'submitted', offers populated
 *   lender sends offer       →  offers[i].decision: 'offered' + terms
 *   lender declines          →  offers[i].decision: 'declined' + reason
 *   every lender declines    →  status: 'declined' (auto-flip)
 *   carrier picks one        →  offers[i].decision: 'accepted' + status: 'accepted'
 *   admin records funding    →  status: 'funded' + outcome.fundedAt
 *   carrier uploads receipt  →  status: 'awaiting_repayment_approval'
 *   admin approves           →  status: 'repaid' + outcome.repaidAt
 *   admin rejects            →  status: 'funded' (receipt cleared)
 *   30+ days past due        →  status: 'defaulted'
 *
 * Non-lifecycle markers live on `tags[]` (e.g. `late`, `overdue`,
 * `flagged`) so they can coexist with any status.
 *
 * Expected indexes (created by the migration):
 * - `{ 'applicant._id': 1, status: 1 }` — carrier "my applications"
 * - `{ 'offers.lender._id': 1 }` — lender inbox by lender
 * - `{ 'offers.id': 1 }` — per-offer reverse lookup
 * - `{ 'invoice._id': 1 }` — link back to source Hero Invoice
 * - `{ 'tenants.id': 1 }` — relations-v2 visibility
 * - `{ status: 1, createdAt: -1 }` — admin tab filtering
 * - `{ tags: 1, status: 1 }` — admin "late" / "flagged" filters
 */
export const FinancingApplicationSchema = createModel({
  /** Human-readable short ref, e.g. `FIN-2026-00042`. */
  ref: refSchema,

  /** The carrier (or freshly-signed-up Org) requesting financing. */
  applicant: partySchema,

  /**
   * The specific user inside `applicant` who clicked Apply. Stored
   * as a `target: 'user'` Party snapshot so the application is
   * auditable down to the person, not just the company. Built from
   * `CurrentUserService.currentUserParty()` at submit time; ids
   * (`user_id` / `phone` / `email`) are walked into `tenants[]`
   * so the same person sees the application even if their org
   * membership changes later.
   *
   * Optional because pre-Phase-B docs were written without a
   * contact Party — leaving them schema-valid prevents broken reads
   * during the pilot. Every new write fills it in.
   */
  contact: partySchema.optional(),

  /** Discriminated invoice block — Hero ref OR uploaded external. */
  invoice: financingInvoiceSchema,

  /** Amount the carrier wants advanced. Defaults to 85% of invoice. */
  requestedAmount: priceSchema,

  /** Repayment term the carrier requested (30/60/90d, 6mo, 1y). */
  requestedTerm: termDurationSchema,

  /**
   * Frozen snapshot of `Org.financingProfile` at submit time. Kept
   * inline so the lender's underwriting packet is self-contained
   * and historical applications don't drift when the carrier
   * updates their live profile later.
   */
  profileSnapshot: orgFinancingProfileSchema,

  /**
   * Lifecycle status. See module-level JSDoc for transitions.
   *
   * Defaults to `pending_review`: every new application sits behind
   * the admin review gate until a super-admin calls
   * `sendToLendersAPI`. The legacy auto-send behaviour is reachable
   * by flipping `FINANCING_CONFIG.autoSendApplications=true` on the
   * backend, which lets `createApplicationAPI` skip straight to
   * `submitted`.
   */
  status: financingApplicationStatusSchema.default('pending_review'),

  /**
   * Non-lifecycle markers (`late`, `overdue`, `flagged`, …) admin
   * can apply or remove independently of `status`. Empty by default.
   * A future cron sweep will flip `late` / `overdue` automatically
   * based on `outcome.fundedAt` + `FINANCING_CONFIG.repayment.*`
   * offsets; today admins manage them via
   * `PATCH /financing/applications/:id/tags`.
   */
  tags: z.array(financingApplicationTagSchema).default([]),

  /**
   * Per-lender responses. One entry per invited lender. Each entry
   * IS the offer (pending, offered-with-terms, declined, or
   * accepted-by-carrier). No separate `FinancingOffer` collection
   * in v0. Populated when the admin sends the application to
   * lenders (`pending_review` → `submitted` transition).
   *
   * **Invariant** — at most one entry may carry
   * `decision: 'accepted'`. The accept service enforces this on
   * every write; per-lender "who won" queries pivot on it directly.
   */
  offers: z.array(financingOfferSchema).default([]),

  /** Filled by Hero ops as the loan progresses through funding. */
  outcome: financingApplicationOutcomeSchema.optional(),

  /**
   * Proof-of-repayment uploaded by the carrier after funding.
   * Present only while `status === 'awaiting_repayment_approval'`
   * (admin pending review) or `status === 'repaid'` (admin approved).
   * Cleared back to `undefined` when the admin rejects a receipt and
   * reverts the application to `funded`.
   */
  repaymentReceipt: financingRepaymentReceiptSchema.optional(),

  /**
   * Relations-v2 visibility entries. Includes every `Party.ids[]`
   * entry from `applicant` AND `contact` (org_id + user_id +
   * phone + email). Used by `db.crud.service` to scope "all
   * applications a party can see" without OR'ing across roles.
   *
   * Super-admins bypass `tenants` scoping at the service layer, so
   * no synthetic `hero_system` entry is needed.
   */
  tenants: z.array(tenantSchema).default([]),

  /** Append-only audit log. */
  events: z.array(financingApplicationEventSchema).default([]),
});

export type FinancingApplication = z.infer<
  typeof FinancingApplicationSchema.model
>;
export type FinancingApplicationData = z.infer<
  typeof FinancingApplicationSchema.data
>;
export type FinancingApplicationPartial = z.infer<
  typeof FinancingApplicationSchema.partial
>;
